import React, {useContext, useState} from 'react'
import {Box} from "@mui/system";
import {Alert, Button, FormHelperText, Grid, IconButton, Stack, Typography} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import {FileRejection, useDropzone} from "react-dropzone";
import makeStyles from "@mui/styles/makeStyles";
import {Attachment, AttachmentTag, db} from "../../lib/db";
import {useLiveQuery} from "dexie-react-hooks";
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";

const useStyles = makeStyles(theme => ({
    dropzone: {
        background: theme.palette.grey["100"],
        border: `1px dashed ${theme.palette.grey["600"]}`,
        minHeight: '100px',
        padding: theme.spacing(4)
    }
}))

let lastTimeout: number | null = null;

type MultiImageInputProps = {
    attachmentIds: number[],
    setAttachmentIds: React.Dispatch<number[]>,
    tag: AttachmentTag,
    errorText?: string
    maxSizeInMb?: number,
}

const MultiImageInput: React.FC<MultiImageInputProps> = ({
                                                             attachmentIds,
                                                             setAttachmentIds,
                                                             tag,
                                                             errorText,
                                                             maxSizeInMb = 5
                                                         }) => {
    const {t} = useContext(OrgCustomizationContext)
    const [fileErrors, setFileErrors] = useState<FileRejection[]>([])
    const images = useLiveQuery(() => db.attachments.bulkGet(attachmentIds), [attachmentIds]);

    const {getRootProps, getInputProps} = useDropzone({
        onDrop: async (acceptedFiles: File[], fileRejections) => {
            const newIds: number[] = await Promise.all(acceptedFiles.map(async (file) => {
                return db.attachments.add({
                    name: file.name,
                    content: file,
                    tag: tag
                })
            }))

            setAttachmentIds([...attachmentIds, ...newIds])

            setFileErrors(fileRejections)

            lastTimeout && clearTimeout(lastTimeout)
            // @ts-ignore
            lastTimeout = setTimeout(() => {
                setFileErrors([])
            }, 5000)
        },
        accept: {
            'image/png': [],
            'image/jpg': [],
            'image/jpeg': [],
        },
        multiple: true,
        maxSize: maxSizeInMb * 1048576 // Byte to MB binary
    })

    const classes = useStyles()

    const handleRemoveImage = (image: Attachment) => async () => {
        setAttachmentIds(attachmentIds.filter(i => i !== image.id))
        await db.attachments.delete(image.id)
    }

    const uploadSize = images ? images.reduce((sum, image) => sum + (image?.content.size || 0), 0) / 1024 / 1024 : 0;

    return (
        <>
            <Box {...getRootProps()} className={classes.dropzone}>
                <Stack direction='column' alignItems='center' spacing={3}>
                    <input {...getInputProps()}/>
                    <Typography variant='body1'>
                        {t('multi_image_prompt')}
                    </Typography>
                    <Button variant='contained'>
                        {t('multi_image_button_text')}
                    </Button>
                </Stack>
            </Box>

            <Box>
                {fileErrors?.map(e => <Alert color='error'>
                    {e.errors.map(x => {
                        switch (x.code) {
                            case 'file-invalid-type':
                                return t('multi_image_invalid_file_type', {filename: e.file.name})
                            case 'file-too-large':
                                return t('multi_image_file_too_large', {
                                    filename: e.file.name,
                                    maxSizeInMb: maxSizeInMb.toString()
                                })
                            case 'file-too-small':
                                return t('multi_image_file_too_small', {filename: e.file.name})
                            case 'too-many-files':
                                return t('multi_image_too_many_files', {filename: e.file.name})
                            default:
                                return t('multi_image_default_error_text', {filename: e.file.name})
                        }
                    }).join(',')}
                </Alert>)}
            </Box>

            <Box>
                <Grid container spacing={2}>
                    {images && (images.filter(i => i?.id) as Attachment[]).map((image, i) =>
                        <Grid key={i} item xs={6} sm={4} md={2}>
                            <Box sx={{aspectRatio: '1 / 1', position: 'relative'}}>
                                <img
                                    src={URL.createObjectURL(image.content)}
                                    style={{height: '100%', width: '100%', objectFit: 'cover'}}/>
                                <Box sx={{position: 'absolute', top: 2, right: 2}}>
                                    <IconButton
                                        onClick={handleRemoveImage(image)}>
                                        <CancelIcon/>
                                    </IconButton>
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>

            {attachmentIds.length > 0 &&
                <Typography variant='body1' align='right'>{t('multi_image_metadata_text', {
                    fileCount: attachmentIds.length.toString(),
                    sizeInMb: uploadSize.toFixed(2)
                })}</Typography>
            }

            <FormHelperText error={!!errorText}>
                {errorText}
            </FormHelperText>
        </>
    )
}

export default MultiImageInput
